/**
 * ComplaintDetailPage — `/customer-service/complaints/:id`.
 *
 * Operator view of a single service-desk case. Provides:
 *   - Case header with status, priority, SLA target.
 *   - Status transition panel: ASSIGN (→ IN_PROGRESS), RESOLVE, REJECT, ESCALATE.
 *   - Internal notes panel: add INTERNAL comment visible only to CSRs.
 *   - Escalation panel: bump escalation tier with reason.
 *   - Resolution panel: mark RESOLVED with a resolution type + note.
 */
import { type JSX, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowUp, MessageSquare, CheckCircle, XCircle } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addCaseComment,
  escalateCase,
  getServiceCase,
  listCaseComments,
  resolveCase,
  transitionCase,
} from '../../lib/api/service-desk';
import { PageHeader, StatusBadge } from '../shared/StaffUI';

function MutationFeedback({
  isPending,
  isError,
  error,
  isSuccess,
  successMsg,
}: {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  isSuccess: boolean;
  successMsg: string;
}): JSX.Element | null {
  if (isPending) return <p className="text-xs text-muted">Saving…</p>;
  if (isSuccess) return <p className="text-xs text-green-700">{successMsg}</p>;
  if (isError)
    return (
      <p className="text-xs text-red-700">
        Error: {(error as Error).message}
      </p>
    );
  return null;
}

const RESOLUTION_TYPES = [
  'RESOLVED_FOR_CUSTOMER',
  'DUPLICATE',
  'OUT_OF_SCOPE',
  'WITHDRAWN',
] as const;

export function ComplaintDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const caseQuery = useQuery({
    queryKey: ['cs', 'cases', 'detail', id],
    queryFn: () => getServiceCase(id),
    retry: false,
    enabled: id !== '',
  });

  const commentsQuery = useQuery({
    queryKey: ['cs', 'cases', 'comments', id],
    queryFn: () => listCaseComments(id),
    retry: false,
    enabled: id !== '',
  });

  // Transition
  const [transitionNote, setTransitionNote] = useState('');
  const assignMutation = useMutation({
    mutationFn: () => transitionCase(id, 'ASSIGN', transitionNote || undefined),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['cs', 'cases'] }); },
  });

  // Internal note
  const [noteBody, setNoteBody] = useState('');
  const noteMutation = useMutation({
    mutationFn: () => addCaseComment(id, noteBody, 'INTERNAL'),
    onSuccess: () => {
      setNoteBody('');
      void qc.invalidateQueries({ queryKey: ['cs', 'cases', 'comments', id] });
    },
  });

  // Escalation
  const [escalateReason, setEscalateReason] = useState('');
  const escalateMutation = useMutation({
    mutationFn: () => escalateCase(id, escalateReason),
    onSuccess: () => {
      setEscalateReason('');
      void qc.invalidateQueries({ queryKey: ['cs', 'cases'] });
    },
  });

  // Resolution
  const [resolutionType, setResolutionType] = useState<string>(RESOLUTION_TYPES[0]);
  const [resolutionNote, setResolutionNote] = useState('');
  const resolveMutation = useMutation({
    mutationFn: () => resolveCase(id, resolutionType, resolutionNote),
    onSuccess: () => {
      setResolutionNote('');
      void qc.invalidateQueries({ queryKey: ['cs', 'cases'] });
    },
  });

  const caseData = caseQuery.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title={caseData ? `Case ${caseData.caseNumber}` : 'Complaint detail'}
        description="Operator view — transitions, notes and escalation."
        actions={
          <Link
            to="/customer-service/complaints"
            className="px-3 py-2 text-sm font-medium text-nipost-green bg-nipost-green-light rounded-lg hover:bg-nipost-green hover:text-white"
          >
            Back to queue
          </Link>
        }
      />

      {caseQuery.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : caseQuery.isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="case-error">
          Failed to load case: {(caseQuery.error as Error).message}
        </div>
      ) : caseData ? (
        <>
          {/* ── Case header ─────────────────────────────────────────────────── */}
          <div
            className="bg-white border border-border rounded-xl p-5 space-y-3"
            data-testid="case-header"
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{caseData.title}</h2>
                <p className="text-xs text-muted mt-1">
                  {caseData.formKey} · {caseData.category} · opened{' '}
                  {new Date(caseData.openedAt).toLocaleString()}
                </p>
                {caseData.escalationTier > 0 && (
                  <p className="text-xs text-amber-700 mt-1 font-medium">
                    Escalation tier {caseData.escalationTier}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <StatusBadge status={caseData.priority} />
                <StatusBadge status={caseData.status} />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <p className="text-muted">Agency</p>
                <p className="font-medium text-gray-900">{caseData.agency}</p>
              </div>
              <div>
                <p className="text-muted">SLA target</p>
                <p className="font-medium text-gray-900">
                  {caseData.slaTarget ? new Date(caseData.slaTarget).toLocaleString() : '—'}
                </p>
              </div>
              <div>
                <p className="text-muted">Assignee</p>
                <p className="font-medium text-gray-900">
                  {caseData.assigneeId ? caseData.assigneeId.slice(0, 8) + '…' : 'Unassigned'}
                </p>
              </div>
              <div>
                <p className="text-muted">Updated</p>
                <p className="font-medium text-gray-900">
                  {new Date(caseData.updatedAt).toLocaleString()}
                </p>
              </div>
            </div>
            {caseData.formData && Object.keys(caseData.formData).length > 0 && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-muted mb-2">Form data</p>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words">
                  {JSON.stringify(caseData.formData, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* ── Transition: Assign / IN_PROGRESS ────────────────────────────── */}
          {caseData.status === 'OPEN' && (
            <div
              className="bg-white border border-border rounded-xl p-5 space-y-3"
              data-testid="assign-panel"
            >
              <h3 className="text-sm font-semibold text-gray-900">Assign to self (OPEN → IN_PROGRESS)</h3>
              <div className="space-y-2">
                <textarea
                  value={transitionNote}
                  onChange={(e) => setTransitionNote(e.target.value)}
                  rows={2}
                  placeholder="Optional note…"
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg"
                  data-testid="assign-note"
                />
                <button
                  type="button"
                  onClick={() => assignMutation.mutate()}
                  disabled={assignMutation.isPending}
                  className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                  data-testid="assign-btn"
                >
                  Assign case
                </button>
                <MutationFeedback
                  isPending={assignMutation.isPending}
                  isError={assignMutation.isError}
                  error={assignMutation.error}
                  isSuccess={assignMutation.isSuccess}
                  successMsg="Case assigned — status is now IN_PROGRESS."
                />
              </div>
            </div>
          )}

          {/* ── Escalate panel ───────────────────────────────────────────────── */}
          {['OPEN', 'IN_PROGRESS'].includes(caseData.status) && (
            <div
              className="bg-white border border-border rounded-xl p-5 space-y-3"
              data-testid="escalate-panel"
            >
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <ArrowUp className="w-4 h-4 text-amber-500" /> Escalate case
              </h3>
              <div className="space-y-2">
                <textarea
                  value={escalateReason}
                  onChange={(e) => setEscalateReason(e.target.value)}
                  rows={2}
                  placeholder="Reason for escalation (required)…"
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg"
                  data-testid="escalate-reason"
                />
                <button
                  type="button"
                  onClick={() => escalateMutation.mutate()}
                  disabled={escalateMutation.isPending || escalateReason.trim().length === 0}
                  className="px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                  data-testid="escalate-btn"
                >
                  Escalate
                </button>
                <MutationFeedback
                  isPending={escalateMutation.isPending}
                  isError={escalateMutation.isError}
                  error={escalateMutation.error}
                  isSuccess={escalateMutation.isSuccess}
                  successMsg="Case escalated successfully."
                />
              </div>
            </div>
          )}

          {/* ── Resolve panel ────────────────────────────────────────────────── */}
          {['IN_PROGRESS', 'AWAITING_CUSTOMER'].includes(caseData.status) && (
            <div
              className="bg-white border border-border rounded-xl p-5 space-y-3"
              data-testid="resolve-panel"
            >
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-nipost-green" /> Resolve case
              </h3>
              <div className="space-y-2">
                <select
                  value={resolutionType}
                  onChange={(e) => setResolutionType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg"
                  data-testid="resolution-type"
                >
                  {RESOLUTION_TYPES.map((rt) => (
                    <option key={rt} value={rt}>
                      {rt.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
                <textarea
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  rows={3}
                  placeholder="Resolution note (required)…"
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg"
                  data-testid="resolution-note"
                />
                <button
                  type="button"
                  onClick={() => resolveMutation.mutate()}
                  disabled={resolveMutation.isPending || resolutionNote.trim().length === 0}
                  className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                  data-testid="resolve-btn"
                >
                  Mark resolved
                </button>
                <MutationFeedback
                  isPending={resolveMutation.isPending}
                  isError={resolveMutation.isError}
                  error={resolveMutation.error}
                  isSuccess={resolveMutation.isSuccess}
                  successMsg="Case marked RESOLVED."
                />
              </div>
            </div>
          )}

          {/* ── Internal note panel ──────────────────────────────────────────── */}
          <div
            className="bg-white border border-border rounded-xl p-5 space-y-3"
            data-testid="notes-panel"
          >
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-nipost-green" /> Add internal note
            </h3>
            <div className="space-y-2">
              <textarea
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                rows={3}
                placeholder="Internal note — visible only to CSRs…"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg"
                data-testid="note-body"
              />
              <button
                type="button"
                onClick={() => noteMutation.mutate()}
                disabled={noteMutation.isPending || noteBody.trim().length === 0}
                className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                data-testid="add-note-btn"
              >
                Add note
              </button>
              <MutationFeedback
                isPending={noteMutation.isPending}
                isError={noteMutation.isError}
                error={noteMutation.error}
                isSuccess={noteMutation.isSuccess}
                successMsg="Note added."
              />
            </div>

            {/* Comments list */}
            {commentsQuery.data && commentsQuery.data.items.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="comments-list">
                {commentsQuery.data.items.map((c) => (
                  <div
                    key={c.id}
                    className={`p-3 rounded-lg text-xs ${
                      c.visibility === 'INTERNAL'
                        ? 'bg-amber-50 border border-amber-200'
                        : 'bg-gray-50 border border-border'
                    }`}
                    data-testid={`comment-${c.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-700">{c.authorRole}</span>
                      <span className="text-muted">{new Date(c.postedAt).toLocaleString()}</span>
                    </div>
                    <p className="text-gray-800 whitespace-pre-wrap">{c.body}</p>
                    {c.visibility === 'INTERNAL' && (
                      <span className="mt-1 inline-block text-amber-700 font-medium">INTERNAL</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Resolution notice ────────────────────────────────────────────── */}
          {caseData.status === 'RESOLVED' && (
            <div
              className="bg-white border border-green-200 rounded-xl p-5 space-y-2"
              data-testid="resolved-notice"
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h3 className="text-sm font-semibold text-green-800">Case resolved</h3>
              </div>
              <p className="text-xs text-gray-700">
                Resolution type: <span className="font-medium">{caseData.resolutionType ?? '—'}</span>
              </p>
              {caseData.resolutionNote && (
                <p className="text-xs text-gray-700 whitespace-pre-wrap">{caseData.resolutionNote}</p>
              )}
              {caseData.resolvedAt && (
                <p className="text-xs text-muted">
                  Resolved at: {new Date(caseData.resolvedAt).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {/* ── Escalated notice ─────────────────────────────────────────────── */}
          {caseData.status === 'ESCALATED' && (
            <div
              className="bg-white border border-amber-200 rounded-xl p-5 space-y-2"
              data-testid="escalated-notice"
            >
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-amber-600" />
                <h3 className="text-sm font-semibold text-amber-800">
                  Case escalated — tier {caseData.escalationTier}
                </h3>
              </div>
              <p className="text-xs text-gray-700">
                Escalated case is now in the escalation queue.
              </p>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
